import React, { useEffect, useRef, useState } from "react";
import { Loader2, LogOut, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { partnerService, formatApiError, resolveImageUrl } from "../../services/catalogService";

const inputCls = "h-11 bg-white border border-black/15 px-3 text-sm focus:outline-none focus:border-[#D92A47]";

// Task 13 — partners gallery administration (socios/colaboradores).
export default function PartnersPanel({ admin, onLogout }) {
  const [partners, setPartners] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | partner
  const [form, setForm] = useState({ name: "", country: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const reload = () => {
    partnerService.list().then(setPartners).catch((e) => toast.error("Could not load partners", { description: formatApiError(e) }));
  };
  useEffect(() => { reload(); }, []);

  const startEdit = (p) => { setEditing(p); setForm({ name: p.name, country: p.country || "", website: p.website || "" }); };
  const startNew = () => { setEditing("new"); setForm({ name: "", country: "", website: "" }); };
  const cancel = () => setEditing(null);

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await partnerService.create(form);
        toast.success("Partner added", { description: form.name });
      } else {
        await partnerService.update(editing.id, form);
        toast.success("Partner saved", { description: form.name });
      }
      cancel();
      reload();
    } catch (err) {
      toast.error("Could not save", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await partnerService.remove(id);
      setConfirmId(null);
      reload();
      toast.success("Partner deleted");
    } catch (err) {
      toast.error("Could not delete", { description: formatApiError(err) });
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || editing === "new") return;
    setUploading(true);
    try {
      const updated = await partnerService.uploadLogo(editing.id, file);
      setEditing(updated);
      reload();
      toast.success("Logo updated");
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-partners-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Partners</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Shown in the public gallery at /partners. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {partners === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
      ) : editing === null ? (
        <>
          <button onClick={startNew} data-testid="partner-new"
            className="mb-6 inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a]">
            <Plus size={14} /> New partner
          </button>
          {partners.length === 0 ? (
            <div className="border border-black/10 bg-white p-10 text-center text-[#817B74]" data-testid="admin-partners-empty">
              No partners yet — add the first one.
            </div>
          ) : (
            <div className="space-y-4">
              {partners.map((p) => (
                <div key={p.id} className="bg-white border border-black/10 p-4 flex items-center gap-4" data-testid={`partner-row-${p.id}`}>
                  <div className="h-14 w-20 bg-white border border-black/10 grid place-items-center shrink-0 overflow-hidden">
                    {p.logo ? <img src={resolveImageUrl(p.logo)} alt="" className="max-h-10 max-w-full object-contain" />
                      : <span className="font-serif text-sm">{p.name.slice(0, 2)}</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-lg text-[#252525] truncate">{p.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-[#817B74]">{p.country || "—"}</p>
                  </div>
                  <button onClick={() => startEdit(p)} data-testid={`partner-edit-${p.id}`} aria-label={`Edit ${p.name}`}
                    className="grid h-10 w-10 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]">
                    <Pencil size={15} />
                  </button>
                  {confirmId === p.id ? (
                    <button onClick={() => remove(p.id)} data-testid={`partner-delete-confirm-${p.id}`}
                      className="h-10 px-4 bg-[#D92A47] text-white text-[11px] font-semibold uppercase tracking-[0.12em]">
                      Confirm delete
                    </button>
                  ) : (
                    <button onClick={() => setConfirmId(p.id)} data-testid={`partner-delete-${p.id}`} aria-label={`Delete ${p.name}`}
                      className="grid h-10 w-10 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="bg-white border border-black/10 p-6 md:p-8" data-testid="partner-form">
          <h2 className="font-serif text-2xl mb-6">{editing === "new" ? "New partner" : `Edit: ${editing.name}`}</h2>

          {editing !== "new" && (
            <div className="mb-6 flex items-center gap-4">
              <div className="h-20 w-32 bg-white border border-black/10 grid place-items-center overflow-hidden">
                {editing.logo && <img src={resolveImageUrl(editing.logo)} alt="" className="max-h-16 max-w-full object-contain" data-testid="partner-logo-preview" />}
              </div>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="partner-logo-upload"
                className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.12em] hover:border-[#D92A47] hover:text-[#D92A47] disabled:opacity-60">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Upload logo"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                onChange={uploadLogo} data-testid="partner-logo-input" />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Name — required</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-field-name" className={`w-full ${inputCls}`} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Country</span>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="partner-field-country" className={`w-full ${inputCls}`} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Website</span>
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="partner-field-website" className={`w-full ${inputCls}`} />
            </label>
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={busy || !form.name.trim()} data-testid="partner-save"
              className="inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : editing === "new" ? "Add partner" : "Save partner"}
            </button>
            <button onClick={cancel} data-testid="partner-cancel"
              className="h-11 px-5 border border-black/15 text-[12px] uppercase tracking-[0.14em] hover:border-[#D92A47]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
